import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Home, Download, FileText, Receipt, DollarSign, CalendarDays, TrendingUp, Wallet, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { STATUS_COLOR } from "./admin/common";

const KPI = ({ icon: Icon, label, value, hint }) => (
  <div className="card-lux p-5" data-testid={`owner-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-wide text-stone-500">{label}</span><Icon size={18} className="text-rose" /></div>
    <div className="font-serif text-3xl text-forest mt-2">{value}</div>
    {hint && <div className="text-xs text-stone-400 mt-1">{hint}</div>}
  </div>
);

export default function OwnerPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sum, setSum] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [dl, setDl] = useState(false);
  const [range, setRange] = useState({ start: "", end: "" });

  useEffect(() => {
    api.get("/owner/summary").then((r) => setSum(r.data)).catch(() => {});
    api.get("/owner/bookings").then((r) => setBookings(r.data)).catch(() => {});
  }, []);

  const doLogout = async () => { await logout(); navigate("/admin/login"); };
  const download = async (fmt) => {
    setDl(fmt);
    try {
      const qs = [];
      if (range.start) qs.push(`start=${range.start}`);
      if (range.end) qs.push(`end=${range.end}`);
      const r = await api.get(`/owner/statement.${fmt}${qs.length ? `?${qs.join("&")}` : ""}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `rose-air-statement.${fmt}`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error("Could not download statement"); }
    finally { setDl(false); }
  };

  const fmtD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const preset = (kind) => {
    const n = new Date();
    let s, e;
    if (kind === "this-month") { s = new Date(n.getFullYear(), n.getMonth(), 1); e = new Date(n.getFullYear(), n.getMonth() + 1, 0); }
    else if (kind === "last-month") { s = new Date(n.getFullYear(), n.getMonth() - 1, 1); e = new Date(n.getFullYear(), n.getMonth(), 0); }
    else { s = new Date(n.getFullYear(), 0, 1); e = new Date(n.getFullYear(), 11, 31); }
    setRange({ start: fmtD(s), end: fmtD(e) });
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="owner-portal">
      <header className="bg-forest text-cream">
        <div className="container-lux py-4 flex items-center justify-between">
          <div>
            <div className="font-serif text-2xl">Owner <span className="text-gold">Portal</span></div>
            <div className="text-xs text-cream/60">{user?.name || user?.email} · read-only</div>
          </div>
          <div className="flex items-center gap-2">
            <a href="/" target="_blank" rel="noopener noreferrer" data-testid="owner-view-site" className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm bg-white/5 hover:bg-white/10"><Home size={16} /> Website</a>
            <button onClick={doLogout} data-testid="owner-logout" className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm bg-white/5 hover:bg-white/10"><LogOut size={16} /> Sign out</button>
          </div>
        </div>
      </header>

      <main className="container-lux py-8 space-y-8">
        {!sum ? <Loader2 className="animate-spin text-rose mx-auto" /> : (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <KPI icon={DollarSign} label="Revenue collected" value={`$${sum.revenue_collected}`} />
            <KPI icon={Receipt} label="Est. tax owed" value={`$${sum.tax_owed || 0}`} hint={`${sum.tax_rate || 0}% of revenue`} />
            <KPI icon={Wallet} label="Outstanding" value={`$${sum.outstanding}`} hint="Balances due" />
            <KPI icon={CalendarDays} label="Upcoming" value={sum.upcoming} hint="Future stays" />
            <KPI icon={TrendingUp} label="Confirmed" value={sum.confirmed} hint={`${sum.total_bookings} total`} />
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-2xl text-forest">Bookings</h2>
            <div className="flex flex-wrap items-end gap-2">
              <div className="flex gap-1 mr-1" data-testid="owner-presets">
                <button onClick={() => preset("this-month")} data-testid="preset-this-month" className="text-xs px-2.5 py-1.5 rounded-lg border border-stone-200 hover:bg-sand">This month</button>
                <button onClick={() => preset("last-month")} data-testid="preset-last-month" className="text-xs px-2.5 py-1.5 rounded-lg border border-stone-200 hover:bg-sand">Last month</button>
                <button onClick={() => preset("this-year")} data-testid="preset-this-year" className="text-xs px-2.5 py-1.5 rounded-lg border border-stone-200 hover:bg-sand">This year</button>
                <button onClick={() => setRange({ start: "", end: "" })} data-testid="preset-clear" className="text-xs px-2.5 py-1.5 rounded-lg border border-stone-200 hover:bg-sand text-stone-500">All time</button>
              </div>
              <label className="text-xs text-stone-500">From<input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} data-testid="owner-start-date" className="block mt-1 rounded-lg border border-stone-200 px-2 py-2 text-sm" /></label>
              <label className="text-xs text-stone-500">To<input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} data-testid="owner-end-date" className="block mt-1 rounded-lg border border-stone-200 px-2 py-2 text-sm" /></label>
              <button onClick={() => download("pdf")} disabled={!!dl} data-testid="owner-download-pdf" className="btn-primary disabled:opacity-50"><FileText size={15} /> {dl === "pdf" ? "Preparing…" : "PDF"}</button>
              <button onClick={() => download("csv")} disabled={!!dl} data-testid="owner-download-statement" className="btn-dark disabled:opacity-50"><Download size={15} /> {dl === "csv" ? "Preparing…" : "CSV"}</button>
            </div>
          </div>
          <div className="card-lux overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="owner-bookings-table">
                <thead className="bg-sand/60 text-stone-500 text-xs uppercase tracking-wide">
                  <tr><th className="text-left p-3">Guest</th><th className="text-left p-3">Suite</th><th className="text-left p-3">Arrival</th><th className="text-left p-3">Departure</th><th className="text-left p-3">Source</th><th className="text-left p-3">Paid / Total</th><th className="text-left p-3">Status</th></tr>
                </thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b.id} className="border-t border-stone-100" data-testid={`owner-booking-${b.id}`}>
                      <td className="p-3 font-medium text-forest">{b.guest_name}</td>
                      <td className="p-3">{b.room_name}</td>
                      <td className="p-3 text-xs whitespace-nowrap">{b.check_in}</td>
                      <td className="p-3 text-xs whitespace-nowrap">{b.check_out}</td>
                      <td className="p-3 capitalize text-xs">{b.source}</td>
                      <td className="p-3 whitespace-nowrap"><span className="font-semibold">${b.amount_paid || 0}</span><span className="text-stone-400"> / ${b.total}</span></td>
                      <td className="p-3"><span className={`text-xs px-2 py-1 rounded-full font-medium ${STATUS_COLOR[b.status] || "bg-stone-100 text-stone-600"}`}>{(b.status || "").replace("_", " ")}</span></td>
                    </tr>
                  ))}
                  {bookings.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-stone-400">No bookings yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
